import { dateNl, monthLongNl, weekdayLongNl } from '../time/nl.js';
import { lastRuleSlot } from './generate.js';
import { parseLocalDate, weekdayOf } from './localDate.js';
import { sortByDay } from './rrule.js';
import { WEEKDAYS, } from './types.js';
const ORDINAL_NL = {
    '1': 'eerste',
    '2': 'tweede',
    '3': 'derde',
    '4': 'vierde',
    '5': 'vijfde',
    '-1': 'laatste',
    '-2': 'een-na-laatste',
    '-3': 'twee-na-laatste',
    '-4': 'drie-na-laatste',
    '-5': 'vier-na-laatste',
};
const WORKDAYS = ['MO', 'TU', 'WE', 'TH', 'FR'];
/** "a", "a en b", "a, b en c". */
export function joinNl(items) {
    if (items.length <= 1)
        return items[0] ?? '';
    return `${items.slice(0, -1).join(', ')} en ${items[items.length - 1] ?? ''}`;
}
function sameSet(a, b) {
    if (a.length !== b.length)
        return false;
    const s = new Set(a);
    return b.every((x) => s.has(x));
}
function monthDayNl(v) {
    if (v === -1)
        return 'de laatste dag';
    if (v < -1)
        return `de ${ORDINAL_NL[String(v)] ?? `${-v}e-laatste`} dag`;
    return `de ${v}e`;
}
/** Weekdagen als lijst, met "werkdag" en "dag" als die de hele set dekken. */
function weekdaysPhrase(days, every) {
    const sorted = [...new Set(days)].sort((a, b) => WEEKDAYS.indexOf(a) - WEEKDAYS.indexOf(b));
    if (sameSet(sorted, WEEKDAYS))
        return `${every} dag`;
    if (sameSet(sorted, WORKDAYS))
        return `${every} werkdag`;
    return `${every} ${joinNl(sorted.map(weekdayLongNl))}`;
}
/** Ordinale weekdagen: "laatste vrijdag", "eerste en derde zaterdag", "tweede dinsdag en vierde donderdag". */
function ordinalDaysPhrase(days) {
    const sorted = sortByDay(days);
    const byWeekday = new Map();
    const order = [];
    for (const d of sorted) {
        const list = byWeekday.get(d.weekday);
        const ord = d.ordinal ?? 0;
        if (list)
            list.push(ord);
        else {
            byWeekday.set(d.weekday, [ord]);
            order.push(d.weekday);
        }
    }
    const parts = order.map((w) => {
        const ords = (byWeekday.get(w) ?? []).sort((a, b) => (a < 0 ? 100 - a : a) - (b < 0 ? 100 - b : b));
        if (ords.some((o) => o === 0))
            return `elke ${weekdayLongNl(w)}`;
        return `${joinNl(ords.map((o) => ORDINAL_NL[String(o)] ?? `${o}e`))} ${weekdayLongNl(w)}`;
    });
    return joinNl(parts);
}
function periodPrefix(interval, unit) {
    const plural = { dag: 'dagen', week: 'weken', maand: 'maanden', jaar: 'jaar' }[unit];
    if (interval === 2)
        return unit === 'jaar' ? 'Om het jaar' : `Om de ${unit}`;
    return `Elke ${interval} ${plural}`;
}
function baseSummary(input) {
    const rule = input.rule;
    if (!rule)
        return 'Eenmalig';
    const interval = rule.interval ?? 1;
    const start = input.startDate ? parseLocalDate(input.startDate) : null;
    const plainDays = (rule.byDay ?? []).filter((d) => d.ordinal === undefined).map((d) => d.weekday);
    const ordinalDays = (rule.byDay ?? []).filter((d) => d.ordinal !== undefined);
    switch (rule.freq) {
        case 'DAILY': {
            if (plainDays.length > 0 && !sameSet(plainDays, WEEKDAYS)) {
                const phrase = weekdaysPhrase(plainDays, 'Elke');
                return interval === 1
                    ? phrase
                    : `${periodPrefix(interval, 'dag')}, alleen op ${joinNl(plainDays.map(weekdayLongNl))}`;
            }
            return interval === 1 ? 'Elke dag' : periodPrefix(interval, 'dag');
        }
        case 'WEEKLY': {
            const days = plainDays.length > 0 ? plainDays : input.startDate ? [weekdayOf(input.startDate)] : [];
            if (interval === 1)
                return days.length > 0 ? weekdaysPhrase(days, 'Elke') : 'Elke week';
            const prefix = periodPrefix(interval, 'week');
            if (days.length === 0)
                return prefix;
            const list = sameSet(days, WEEKDAYS)
                ? 'alle dagen'
                : sameSet(days, WORKDAYS)
                    ? 'werkdagen'
                    : joinNl([...new Set(days)]
                        .sort((a, b) => WEEKDAYS.indexOf(a) - WEEKDAYS.indexOf(b))
                        .map(weekdayLongNl));
            return `${prefix} op ${list}`;
        }
        case 'MONTHLY': {
            let what;
            if (ordinalDays.length > 0 && !(rule.byMonthDay && rule.byMonthDay.length > 0)) {
                what = `${ordinalDaysPhrase(ordinalDays)} van de maand`;
                return interval === 1
                    ? `Elke ${what}`
                    : `${periodPrefix(interval, 'maand')} op de ${ordinalDaysPhrase(ordinalDays)}`;
            }
            const monthDays = rule.byMonthDay && rule.byMonthDay.length > 0
                ? [...rule.byMonthDay]
                : start
                    ? [start.day]
                    : [];
            what = monthDays.length > 0 ? `op ${joinNl(sortedMonthDays(monthDays).map(monthDayNl))}` : '';
            if (plainDays.length > 0)
                what += ` als dat een ${joinNl(plainDays.map(weekdayLongNl))} is`;
            const prefix = interval === 1 ? 'Elke maand' : periodPrefix(interval, 'maand');
            return what ? `${prefix} ${what}` : prefix;
        }
        case 'YEARLY': {
            const prefix = interval === 1 ? 'Elk jaar' : periodPrefix(interval, 'jaar');
            const months = rule.byMonth && rule.byMonth.length > 0 ? [...rule.byMonth] : start ? [start.month] : [];
            const monthNames = joinNl(months.sort((a, b) => a - b).map(monthLongNl));
            if (ordinalDays.length > 0) {
                return `${prefix} op de ${ordinalDaysPhrase(ordinalDays)} van ${monthNames}`;
            }
            if (rule.byMonthDay && rule.byMonthDay.length > 0) {
                const days = sortedMonthDays(rule.byMonthDay);
                if (days.length === 1 && (days[0] ?? 0) > 0)
                    return `${prefix} op ${days[0]} ${monthNames}`;
                return `${prefix} op ${joinNl(days.map(monthDayNl))} van ${monthNames}`;
            }
            if (start)
                return `${prefix} op ${start.day} ${monthNames}`;
            return prefix;
        }
    }
}
export function sortedMonthDays(days) {
    return [...new Set(days)].sort((a, b) => {
        if (a > 0 && b < 0)
            return -1;
        if (a < 0 && b > 0)
            return 1;
        return a > 0 ? a - b : b - a;
    });
}
/**
 * De datum die "tot en met" noemt. Een gesplitst of gestopt deel eindigt op split − 1 min
 * (wandklok, §5.3), dus op de splitdag zelf; dan noemen we de laatste echte keer.
 */
export function lastSummaryDate(input, options) {
    const until = input.until ?? '';
    const date = until.slice(0, 10);
    const time = until.length > 10 ? until.slice(11, 19).padEnd(8, ':00') : '';
    if (!input.rule || !input.startDate || time === '' || time >= '23:59')
        return date;
    try {
        const last = lastRuleSlot({
            rule: input.rule,
            startDate: input.startDate,
            startTime: options.allDay ? '00:00:00' : `${options.startTime ?? '00:00'}:00`,
            until,
            count: null,
            weekParity: input.weekParity ?? null,
        });
        return last ?? date;
    }
    catch (err) {
        void err;
        return date;
    }
}
/** Volledige samenvatting inclusief pariteit, tijd en einde. */
export function summarizeRecurrence(input, options = {}) {
    let text = baseSummary(input);
    if (input.rule && input.weekParity) {
        text += input.weekParity === 'even' ? ' in even weken' : ' in oneven weken';
    }
    if (!options.allDay && options.startTime) {
        text += options.endTime
            ? ` van ${options.startTime} tot ${options.endTime}`
            : ` om ${options.startTime}`;
    }
    if (input.rule) {
        if (input.count !== undefined && input.count !== null) {
            text += input.count === 1 ? ', 1 keer' : `, ${input.count} keer`;
        }
        else if (input.until) {
            text += `, tot en met ${dateNl(lastSummaryDate(input, options))}`;
        }
    }
    return text;
}
//# sourceMappingURL=summary.js.map